import { useFormikContext } from 'formik';
import { PlaygroundConfig } from 'modules/Playground/types/playgroundConfig';
import { useModelVersionStatus } from 'modules/Playground/hooks/utils/useModelVersionStatus/useModelVersionStatus';
import { useState } from 'react';
import { useCurrentUserId } from 'modules/Auth/hooks';
import { appId } from 'e2e/pages/AppPage';
import { ApistatusStatus } from 'types/swagger/clarifai-api';
import { Output, StatusCode } from 'api/grpc/resources';
import { grpcClient } from 'api/grpc/client';
import { MultiOutputResponse, PostModelOutputsRequest } from 'api/grpc/service';
import { getMessageHistory } from 'modules/Playground/store/messageStore';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { FilePreview } from 'modules/Playground/Components/ChatInput';
import type { ChatCompletionChunk, ChatCompletionMessageParam, ChatCompletionAssistantMessageParam } from 'openai/resources/index';
import { constructNewModelInterfaceRequest } from './constructNewModelInterfaceRequest';
import { constructModelLegacyReq } from './constructModelLegacyReq';
import { getModelSignaturesFromModelVersion } from './getModelSignaturesFromModelVersion';
import { cleanEmptyFieldsInFieldMap } from './cleanEmptyFieldsInFieldMap';

type EventStreamResponseType = {
  finishReason?: ChatCompletionChunk.Choice['finish_reason'];
  completionTokens?: number;
  promptTokens?: number;
  content?: ChatCompletionChunk.Choice.Delta['content'];
  reasonContent?: string;
  error?: string;
};

export type AbortReason =
  // User manually clicked cancel
  | 'user'
  // User switched to different account / model
  | 'switch';

type ResponseHandlerOutput = Promise<{ status: 'deploying'; e: unknown } | { status: 'done' }>;

type EventPayloadType = (ChatCompletionChunk & { error?: string }) | ApistatusStatus;

const sseEventTypeGuard = (event: EventPayloadType): event is ApistatusStatus => {
  // @ts-expect-error - choices is not present in Union types but needed here for typeguard
  if (!Array.isArray(event?.choices)) return true;
  return false;
};

export const abortError = {
  message: 'Operation cancelled by the user...',
  cause: {
    status: {
      description: 'Operation cancelled',
      details: "Operation was cancelled as per the user's request",
    },
  } as ApistatusStatus,
};

export const deployingError = {
  message: 'Model is Deploying, try again after some time',
};

// Helper wait function that supports abort
const wait = (ms: number, signal?: AbortSignal): Promise<void> => {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);

    if (signal) {
      signal.addEventListener('abort', () => {
        clearTimeout(timer);
        reject(
          new Error(abortError.message, {
            cause: {
              ...abortError.cause,
              reason: signal.reason,
            },
          }),
        );
      });
    }
  });
};

const RETRY_LIMIT = 10;
const STREAMING_RETRY_LIMIT = 4;
const STREAMING_INTERVAL = 1500;
const INTERVAL = 3000;

export const getModelOutputFromGrpcResponse = (outputs: Output.AsObject[]): string | undefined => {
  return (
    // New pythonic interface models read output from the partsList
    outputs?.[0]?.data?.partsList?.[0]?.data?.stringValue ??
    // Legacy models read output from the text field
    outputs?.[0]?.data?.text?.raw
  );
};

export const useFetchModelOutput = () => {
  const [outputString, setOutputString] = useState('');
  const [messagePayloadObject, setMessagePayloadObject] = useState<Record<string, unknown>>();
  const { values } = useFormikContext<PlaygroundConfig>();
  const userOrOrgId = useCurrentUserId();
  const currentUserId = useCurrentUserId();
  const [inTokens, setInTokens] = useState<number>();
  const [outTokens, setOutTokens] = useState<number>();
  const [startTime, setStartTime] = useState<number>();
  const [endTime, setEndTime] = useState<number>();

  const modelVersionStatus = useModelVersionStatus({
    userOrOrgId: values.model?.user_id ?? userOrOrgId, // NOTE: has to match the model owner/url param
    appId: values.model?.app_id ?? appId,
    modelId: values.model?.id ?? '',
    modelVersionId: values.modelVersion?.id ?? values.model?.model_version?.id ?? '',
  });

  const isModelVersionReady = !modelVersionStatus.loading && modelVersionStatus.status === 'success';

  const fetchOutputForModels = async (text: string, images: FilePreview[], abortSignal: AbortSignal) => {
    // Reset the output string and token counts:
    setStartTime(undefined);
    setEndTime(undefined);
    setInTokens(undefined);
    setOutTokens(undefined);
    setOutputString('');

    const retries = 0;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (abortSignal?.aborted) {
        throw new Error(abortError.message, {
          cause: {
            ...abortError.cause,
            reason: abortSignal.reason,
          },
        });
      }
      try {
        let request: PostModelOutputsRequest;

        const methodSignatures = getModelSignaturesFromModelVersion(values.modelVersion, values.model);

        if (methodSignatures) {
          request = constructNewModelInterfaceRequest({
            appId,
            currentUserId,
            images,
            text,
            userOrOrgId,
            values,
          });
        } else {
          request = constructModelLegacyReq({
            appId,
            currentUserId,
            images,
            text,
            userOrOrgId,
            values,
          });
        }

        const handleResponseWithCancel = () => {
          return new Promise<MultiOutputResponse>((resolve, reject) => {
            const signalHandler = () => {
              abortSignal.removeEventListener('abort', signalHandler);
              reject(
                new Error(abortError.message, {
                  cause: {
                    ...abortError.cause,
                    reason: abortSignal.reason,
                  },
                }),
              );
            };

            abortSignal.addEventListener('abort', signalHandler);

            grpcClient
              .postModelOutputsDirect(request)
              .then((val) => resolve(val))
              .catch((e) => reject(e));
          });
        };

        setStartTime(Date.now());
        const response = await handleResponseWithCancel();

        const responseObject = response.toObject();

        setEndTime(Date.now());
        if (responseObject.status?.code !== StatusCode.SUCCESS) {
          throw new Error('Failed to fetch model output', {
            cause: responseObject,
          });
        }

        const completionToken = responseObject.outputsList?.[0]?.completionTokens;
        if (completionToken) setOutTokens(completionToken);

        const promptToken = responseObject.outputsList?.[0]?.promptTokens;
        if (promptToken) setInTokens(promptToken);

        const output = getModelOutputFromGrpcResponse(responseObject.outputsList);

        setOutputString(output ?? '');

        return;
      } catch (e) {
        if ((e as Error).message === abortError.message) {
          throw e; // cancel operation let the error bubble up
        }
        const errorCause = (e as Error).cause as MultiOutputResponse.AsObject;
        const errorStatus = errorCause.status;
        if (retries < RETRY_LIMIT) {
          if (errorStatus?.code === StatusCode.MODEL_DEPLOYING) {
            await wait(INTERVAL, abortSignal);
          } else {
            if ((abortSignal.reason as AbortReason) === 'user') {
              throw new Error(abortError.message, {
                cause: {
                  ...abortError.cause,
                  reason: abortSignal.reason,
                },
              });
            }
            throw new Error('Failed to fetch model output', {
              cause: errorCause,
            });
          }
        } else {
          throw new Error(deployingError.message, {
            cause: errorCause,
          });
        }
      }
    }
  };

  const streamOutputForModels = async (text: string, images: FilePreview[], abortSignal: AbortSignal) => {
    // Reset the output string and token counts:
    setStartTime(undefined);
    setEndTime(undefined);
    setInTokens(undefined);
    setOutTokens(undefined);
    setOutputString('');

    let retries = 0;

    let request: PostModelOutputsRequest;

    const methodSignatures = getModelSignaturesFromModelVersion(values.modelVersion, values.model);

    if (methodSignatures) {
      request = constructNewModelInterfaceRequest({
        appId,
        currentUserId,
        images,
        text,
        userOrOrgId,
        values,
      });
    } else {
      request = constructModelLegacyReq({
        appId,
        currentUserId,
        images,
        text,
        userOrOrgId,
        values,
      });
    }

    try {
      const handleResponse = (): ResponseHandlerOutput => {
        const response = grpcClient.generateModelOutputsDirect(request);

        return new Promise((resolve, reject) => {
          const isDeploying = { status: false };

          const signalHandler = () => {
            response.cancel();
            // setter needed here to get latest output string
            setOutputString((prev) => {
              reject(
                new Error(abortError.message, {
                  cause: {
                    ...abortError.cause,
                    reason: abortSignal.reason,
                    prevMessage: prev,
                  },
                }),
              );
              return prev;
            });
            abortSignal.removeEventListener('abort', signalHandler);
          };

          abortSignal.addEventListener('abort', signalHandler);
          let deployingErrorObject: MultiOutputResponse.AsObject;
          response.on('data', (data) => {
            const dataObject = data.toObject();
            if (
              dataObject.status?.code === StatusCode.MODEL_DEPLOYING ||
              dataObject.status?.code === StatusCode.MODEL_BUSY_PLEASE_RETRY ||
              dataObject.status?.code === StatusCode.MODEL_LOADING
            ) {
              isDeploying.status = true;
              deployingErrorObject = dataObject;
            } else if (dataObject.status?.code === StatusCode.SUCCESS) {
              const completionToken = dataObject?.outputsList?.[0]?.completionTokens;
              if (completionToken) setOutTokens(completionToken);
              const promptToken = dataObject?.outputsList?.[0]?.promptTokens;
              if (promptToken) setInTokens(promptToken);

              isDeploying.status = false;
              const output = getModelOutputFromGrpcResponse(dataObject.outputsList);
              if (abortSignal?.aborted) {
                response.cancel();
                // setter needed here to get latest output string
                setOutputString((prev) => {
                  reject(
                    new Error(abortError.message, {
                      cause: {
                        ...abortError.cause,
                        reason: abortSignal.reason,
                        prevMessage: prev,
                      },
                    }),
                  );
                  return prev;
                });
              }
              if (output?.length) {
                setOutputString((prev) => {
                  if (abortSignal.aborted) {
                    return '';
                  }
                  return prev + output;
                });
              }
            } else {
              const error = data.toObject();
              reject(
                new Error('Failed to fetch model output', {
                  cause: error,
                }),
              );
            }
          });
          const endHandler = () => {
            if (isDeploying.status) {
              resolve({
                status: 'deploying',
                e: deployingErrorObject,
              });
              return;
            }
            setEndTime(Date.now());
            resolve({ status: 'done' });
          };
          response.on('status', (status) => {
            // Fallback for grpc-web, when the end event is not emitted - https://github.com/grpc/grpc-web/issues/543
            if (status.code === 0) {
              endHandler();
            }
          });
          response.on('end', endHandler);
          response.on('error', (error) => {
            reject(
              new Error('Failed to fetch model output', {
                cause: error,
              }),
            );
          });
        });
      };
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (abortSignal?.aborted) {
          throw new Error(abortError.message, {
            cause: {
              ...abortError.cause,
              reason: abortSignal.reason,
            },
          });
        }
        setStartTime(Date.now());
        const result = await handleResponse();
        if (result.status === 'done') {
          return;
        } else if (result.status === 'deploying' && retries < STREAMING_RETRY_LIMIT) {
          retries += 1;
          await wait(STREAMING_INTERVAL, abortSignal);
        } else {
          throw new Error(deployingError.message, {
            cause: result.e,
          });
        }
      }
    } catch (e) {
      if ((e as Error).message === abortError.message) {
        throw e; // cancel operation let the error bubble up
      }
      if ((e as Error).message === deployingError.message) {
        throw e; // cancel operation let the error bubble up
      }
      throw new Error('Failed to fetch model output', {
        cause: (e as Error).cause,
      });
    }
  };

  const streamOpenAIResponse = async (_text: string, _images: FilePreview[], abortSignal: AbortSignal, modelUrl: string) => {
    // Reset the output string and token counts:
    setStartTime(undefined);
    setEndTime(undefined);
    setInTokens(undefined);
    setOutTokens(undefined);
    setOutputString('');

    const messageHistory = getMessageHistory();

    const messages: ChatCompletionMessageParam[] = messageHistory.map((each) => {
      // @ts-expect-error - payload is stored as plain object to be compatible other types of payload apart from openai
      if (each.payload) return each.payload as ChatCompletionMessageParam;
      return {
        role: each.role,
        content: each.content,
      };
    });

    const fieldValueMap = cleanEmptyFieldsInFieldMap(values.fieldValueMap);

    // HACK: temporary fix to format the parameters for OpenAI compatible models
    fieldValueMap.top_p = Number.parseFloat(fieldValueMap.top_p as string);
    fieldValueMap.temperature = Number.parseFloat(fieldValueMap.temperature as string);
    if (fieldValueMap.max_tokens) {
      fieldValueMap.max_completion_tokens = fieldValueMap.max_tokens;
      delete fieldValueMap.max_tokens;
    }

    setStartTime(Date.now());
    const signalHandler = () => {
      setOutputString((prev) => {
        return `${prev}\n\n\`${abortError.message}\``;
      });
      abortSignal.removeEventListener('abort', signalHandler);
    };

    abortSignal.addEventListener('abort', signalHandler);
    let reason = '';
    let content = '';
    let error: boolean | string = false;
    const messagePayload: ChatCompletionAssistantMessageParam = {
      role: 'assistant',
    };
    await fetchEventSource('/api/openai/stream', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      signal: abortSignal,
      body: JSON.stringify({
        messages,
        modelUrl,
        params: fieldValueMap,
      }),
      onmessage(msg) {
        let event: EventPayloadType;
        try {
          // If the message is not valid JSON, it will throw an error
          // and we will catch it and set event to an empty object
          event = JSON.parse(msg.data) as EventPayloadType;
        } catch {
          event = {};
        }
        if (sseEventTypeGuard(event)) {
          if (event.code) {
            throw new Error('Failed to fetch model output', {
              cause: {
                status: {
                  code: event.code,
                  description: event.description,
                  details: event.details,
                  reqId: event.req_id,
                },
              },
            });
          }
          // The server sent a non-json message, which can be displayed directly as string
          throw new Error('Failed to fetch model output', {
            cause: {
              status: {
                details: msg.data,
              },
            },
          });
        }
        const firstChoice = event?.choices?.[0];
        const delta = firstChoice?.delta;
        const data: EventStreamResponseType = {
          finishReason: event.choices[0]?.finish_reason,
          completionTokens: event.usage?.completion_tokens,
          promptTokens: event.usage?.prompt_tokens,
          content: event.choices[0]?.delta?.content,
          error: event.error,
          // @ts-expect-error - reasoning_content is not defined in the OpenAI types, but it is used in the response
          reasonContent: event.choices[0]?.delta?.reasoning_content,
        };

        if (data.error) {
          error = data.error;
          return;
        }
        setOutputString(() => {
          const reasonChunk = data.reasonContent ? data.reasonContent : '';
          const contentChunk = data.content ? data.content : '';
          reason += reasonChunk;
          content += contentChunk;
          if (abortSignal.aborted) {
            return '';
          }
          return `${reason ? `<think>${reason}</think>` : ''}${content}`;
        });
        setInTokens(data.promptTokens);
        setOutTokens(data.completionTokens);

        messagePayload.content = content;
        if (reason) {
          // @ts-expect-error - reasoning_content is not defined in the OpenAI types, but it is used in the messagePayload
          messagePayload.reasoning_content = reason;
        }
        if (delta) {
          if (delta.role) {
            messagePayload.role = delta.role as 'assistant';
          }

          if (delta.refusal) {
            messagePayload.refusal = delta.refusal;
          }

          if (delta.tool_calls) {
            // Cast tool_calls to correct type to avoid TS error
            const newToolCalls = (delta.tool_calls as NonNullable<typeof messagePayload.tool_calls>) ?? [];

            messagePayload.tool_calls = messagePayload.tool_calls ? [...messagePayload.tool_calls, ...newToolCalls] : [...newToolCalls];
          }
        }
        // @ts-expect-error - need to store message payload as an object to be used later
        setMessagePayloadObject(messagePayload);
      },
      onerror(e) {
        setEndTime(Date.now());
        throw e;
      },
      onclose() {
        setEndTime(Date.now());
      },
    });

    if (error) {
      throw new Error('Failed to fetch model output', {
        cause: {
          status: {
            details: error,
          },
        },
      });
    }
  };

  return {
    outputString,
    isModelVersionReady,
    fetchOutputForModels,
    streamOutputForModels,
    startTime,
    endTime,
    inTokens,
    outTokens,
    streamOpenAIResponse,
    messagePayloadObject,
  };
};
